/**
 * The ⓘ explanations for Add from the field — plain words, 7th grade, with the
 * real screens (client/public/help/field-add/*.png) marked up. Positions are %
 * of the picture. Approved wording: the mockup's "Where does this go?" help.
 */
import type { InfoTopic } from "@/components/info-button";

export const FIELD_ADD_HELP: Record<"what" | "where" | "device" | "note", InfoTopic> = {
    what: {
        title: "Adding a photo, note or file",
        lines: [
            "Photo opens the camera. Note is for typing, or talking with the iPad's mic key. File is for a form, a permit or a PDF someone hands you.",
            "Next you pick where it goes. This job is already picked, so most of the time you just tap Save.",
            "Everything you add stays inside the company. The customer never sees it.",
        ],
        shots: [
            {
                src: "/help/field-add/what.png",
                alt: "The What are you adding screen with Photo, Note and File",
                marks: [
                    { x: 18, y: 38, label: "Opens the camera", side: "below" },
                    { x: 50, y: 38, label: "Type, or tap the mic key and talk", side: "below" },
                    { x: 82, y: 38, label: "A form, permit or PDF", side: "below" },
                    { x: 50, y: 68, label: "Never goes to the customer", side: "above" },
                ],
            },
        ],
    },
    where: {
        title: "Picking where it goes",
        lines: [
            "This job is for today only. Use it for before and after pictures and anything about today's work.",
            "Tap Device when it's about one assembly, like a hard shutoff, a cracked lid or a new tag. The next screen lists every device on this job.",
            "Pick This location for things the next tech needs to get in and find things: gate codes, the key, the dog, where the meter is.",
            "Pick This customer only when it's true at all their locations, like “always check in with the manager first.”",
            "Not sure? Leave it on This job. The office can move it later.",
            "Started from the + on a device? That device is already picked. You won't see these screens.",
        ],
        shots: [
            {
                src: "/help/field-add/where.png",
                alt: "The Where does this go screen with four tiles",
                marks: [
                    { x: 26, y: 39, label: "This job: already picked, today only", side: "right" },
                    { x: 74, y: 39, label: "Device: opens the list of devices", side: "left" },
                    { x: 26, y: 60, label: "This location: every tech, every visit", side: "right" },
                    { x: 74, y: 60, label: "This customer: all their locations", side: "left" },
                ],
            },
        ],
    },
    device: {
        title: "Which device?",
        lines: [
            "Every device on this job is listed: its size, its type, its serial number and where it sits.",
            "Tap the one whose serial number matches the tag in front of you. It saves right away.",
            "Picked Device by mistake? Tap Back to go back to the four choices.",
        ],
        shots: [
            {
                src: "/help/field-add/device.png",
                alt: "The Which device screen with one row per device",
                marks: [
                    { x: 10, y: 12, label: "Back to the four choices", side: "right" },
                    { x: 25, y: 60, label: "Match this serial number to the tag", side: "below" },
                ],
            },
        ],
    },
    note: {
        title: "Writing a note",
        lines: [
            "Type the note in the box. To talk instead, tap the mic key on the iPad keyboard. Your words show up in the box so you can check them before you save. No recording is kept.",
            "Pick where it goes, the same as a photo.",
            "Turn on “Office needs to see this” only when the office has to do something about it. It puts the note on their list until someone marks it read. Leave it off for everyday notes.",
        ],
        shots: [
            {
                src: "/help/field-add/note.png",
                alt: "The Note screen with the text box, the four tiles and the office switch",
                marks: [
                    { x: 50, y: 19, label: "Type here, or use the mic key", side: "below" },
                    { x: 9, y: 81, label: "Only when the office must act", side: "above" },
                ],
            },
        ],
    },
};
